import {Component, OnInit, OnDestroy, AfterViewInit} from '@angular/core';
import {AccountTabsComponent} from "../../settings/account-tabs/account-tabs.component";
import {ComboboxMultiselectComponent} from "../../../common/form/combobox-multiselect/combobox-multiselect.component";
import {DropdownSelectComponent} from "../../../common/form/dropdown-select/dropdown-select.component";
import {InputFieldComponent} from "../../../common/form/input-field/input-field.component";
import {NgClass, NgFor, NgIf} from "@angular/common";
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {filter, Subject, Subscription, take} from "rxjs";
import {PopupService} from "../../../services/popup.service";
import {Router, RouterLink} from "@angular/router";
import {UserService} from "../../../services/user.service";
import {AuthService} from "../../../services/auth.service";
import {Store} from "@ngrx/store";
import {AppState} from "../../../state/app.state";
// Removed user profile imports - ICP now uses only ICP API
import * as IcpActions from '../../../state/icp/icp.actions';
import * as IcpSelectors from '../../../state/icp/icp.selectors';
import {updateUser} from "../../../state/auth/auth.actions";
import {loadProfileLists} from "../../../state/profileList/profileList.actions";
import {selectProfileListsFailure, selectProfileListsSuccess} from "../../../state/profileList/profileList.selectors";
import {loadCountries} from "../../../state/list/list.actions";
import {selectCountriesFailure, selectCountriesSuccess} from "../../../state/list/list.selectors";
import {HeaderService} from "../../../services/header.service";
import {IcpOptimizationChartComponent} from "../../../common/icp-optimization-chart/icp-optimization-chart.component";
import {DarkModeToggleComponent} from "../../../common/dark-mode-toggle/dark-mode-toggle.component";
import {SidebarService} from "../../../services/sidebar.service";
import {TourService} from "../../../services/tour.service";
import {AppTourService} from "../../../services/app-tour.service";
import {selectTourActive} from "../../../state/tour/tour.selectors";
import {takeUntil} from "rxjs/operators";
import {TourComponent} from "../../../tour/tour.component";
import {
  MAX_CUSTOM_TARGET_LENGTH,
  OTHER_OPTION_ID,
  TARGET_DEPARTMENT_OPTIONS,
  TARGET_JOB_TITLE_OPTIONS,
  customSelections,
  parseCsvSelections,
  sanitizeCustomTarget,
  selectionsToCsv,
} from "../../../shared/icp-targeting-options";

// Mirror the limits the ICP API validates for the free-text fields
// (WorkspaceICPController::MAX_TEXT_LENGTH / MAX_LONG_TEXT_LENGTH).
const MAX_ICP_TEXT_LENGTH = 512;
// Target job titles / departments CSVs: room for custom 'Other' entries.
const MAX_ICP_TARGETING_LENGTH = 1024;
// 'what_solutions_do_i_sell' and 'challenges_solved' get twice the room.
const MAX_ICP_LONG_TEXT_LENGTH = 1024;

@Component({
  selector: 'app-icp-create',
  standalone: true,
  imports: [
    AccountTabsComponent,
    ComboboxMultiselectComponent,
    DropdownSelectComponent,
    InputFieldComponent,
    NgIf,
    NgFor,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    RouterLink,
    IcpOptimizationChartComponent,
    DarkModeToggleComponent,
    TourComponent
  ],
  templateUrl: './icp-create.component.html',
  styleUrl: './icp-create.component.scss'
})
export class IcpCreateComponent implements OnInit, AfterViewInit, OnDestroy {
  form: FormGroup;
  errorMessages: any;
  isLoading = true; // Start with loading true to prevent flashing
  dataLoaded = false;
  user: any;
  successMessage?: string;
  errorMessage?: string;
  hasExistingIcp = false; // Track if user has existing ICPs
  step = 1;
  readonly maxTextLength = MAX_ICP_TEXT_LENGTH;
  readonly maxLongTextLength = MAX_ICP_LONG_TEXT_LENGTH;

  // Arrays to hold dropdown options
  profileLists: any[] = [];
  actualDuties: any[] = [];
  companySizes: Record<string, any>[] = [];
  customerCompanySizes: any[] = [];
  companyRevenues: Record<string, any>[] = [];
  industries: Record<string, any>[] = [];
  customerIndustries: any[] = [];
  buyingCycles: any[] = [];
  trendEmployees: any[] = [];
  trendRevenues: any[] = [];
  closedDeals: any[] = [];
  countries: any[] = [];

  readonly targetJobTitleOptions = TARGET_JOB_TITLE_OPTIONS;
  readonly targetDepartmentOptions = TARGET_DEPARTMENT_OPTIONS;
  selectedJobTitles: Set<string> = new Set();
  selectedDepartments: Set<string> = new Set();

  isDesktop = window.innerWidth >= 1024;

  // Listed in visual order so the first invalid field found is the first one the user sees.
  private readonly stepOneFields = [
    'name',
    'description',
    'linkedin_url',
    'what_solutions_do_i_sell',
  ];

  private readonly stepTwoFields = [
    'customer_company_size_ids',
    'company_revenue_ids',
    'customer_industry_ids',
    'challenges_solved',
    'main_buyer_department',
    'main_buyer_job_title',
  ];

  // Cache for form validation states to prevent recalculation
  private validationCache: { [key: string]: boolean } = {};

  // Single subscription collection for better memory management
  private subscriptions = new Subscription();

  private destroy$ = new Subject<void>();

  isTourActive: boolean = false; // Don't assume tour is active initially

  tourLoading: boolean = true;

  private originalOverflowStyle: string = '';
  private originalMaxHeight: string = '';
  private scrollPosition: number = 0;

  tourInitializing: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private popupService: PopupService,
    private routerService: Router,
    private userService: UserService,
    private authService: AuthService,
    private store: Store<AppState>,
    protected router: Router,
    private headerService: HeaderService,
    private sidebarService: SidebarService,
    private tourService: TourService,
    private appTourService: AppTourService
  ) {
    this.initializeForm();
    this.setupErrorMessages();
  }

  ngOnInit(): void {
    // Immediate tour check
    this.fastCheckTourState();

    // Continue with the rest of your initialization
    // Always prepare the form for scrolling, regardless of tour state
    this.prepareFormForScrolling();

    // Set initializing state
    this.tourInitializing = true;
    document.body.classList.add('tour-initializing');

    // Set loading states
    this.isLoading = true;
    this.dataLoaded = false;
    this.tourLoading = true;

    // Check if tour is active (without changing local storage)
    this.store.select(selectTourActive)
      .pipe(takeUntil(this.destroy$))
      .subscribe(active => {
        this.isTourActive = active;
        this.tourLoading = false;

        if (active) {
          // Tour is active, ensure form is ready
          this.ensureFormReadyForTour();
        }
      });

    // Show header with custom title
    this.headerService.setHeaderState(
      'Create ICP',
      'Complete all the entries to create your Ideal Customer Profile.',
      false
    );

    // Get authenticated user
    this.authService.authUser()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(user => {
        this.user = user;

        // Validate LinkedIn URL if available
        const linkedinRegex = /^(https?:\/\/)?(www\.)?linkedin\.com\/.*$/;
        const hasValidLinkedinUrl = this.user.linkedin_url && linkedinRegex.test(this.user.linkedin_url);

        // Prepopulate LinkedIn URL if available and valid
        if (hasValidLinkedinUrl) {
          this.form.patchValue({ linkedin_url: this.user.linkedin_url });
          this.form.get('linkedin_url')?.disable();
        } else {
          this.form.get('linkedin_url')?.enable();

          // Set LinkedIn URL as a required field only if user doesn't have one
          const linkedinControl = this.form.get('linkedin_url');
          if (linkedinControl) {
            // We'll keep the original validators but make sure it's marked as required
            linkedinControl.setValidators([
              Validators.required,
              Validators.pattern(/^(https?:\/\/)?(www\.)?linkedin\.com\/.*$/),
              Validators.maxLength(255)
            ]);
            linkedinControl.updateValueAndValidity();
          }
        }
      });

    // Add value changes listener to automatically update validation
    this.setupFormListeners();

    // Load existing ICPs to check if user has any (to determine is_default value)
    this.store.dispatch(IcpActions.loadIcps({
      page: 1,
      perPage: 100 // Load all ICPs to check existence
    }));

    // Subscribe to ICP list to check if user has existing ICPs
    this.store.select(IcpSelectors.selectIcps)
      .pipe(takeUntil(this.destroy$))
      .subscribe(icps => {
        // Set flag based on whether user has any ICPs
        this.hasExistingIcp = icps && icps.length > 0;
      });

    // Prefill "Who I am" (step 1) from the user's default ICP once it loads.
    // Skips name/description/linkedin_url — those are filled per-ICP or from the user profile.
    this.store.select(IcpSelectors.selectDefaultIcp)
      .pipe(
        filter(icp => !!icp),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(defaultIcp => this.prefillFromDefaultIcp(defaultIcp));

    // Set up data loading promises
    const dataLoading = Promise.all([
      this.getProfileLists(),
      this.getCountries()
    ]);

    // Wait for dropdown data to load before marking form as ready
    dataLoading.then(() => {
      // Mark data as loaded with delay to ensure smooth transitions
      setTimeout(() => {
        this.isLoading = false;
        this.dataLoaded = true;

        // Reset validation cache after data is loaded
        this.resetValidationCache();

        // Ensure tour is ready if active
        if (this.isTourActive) {
          this.ensureFormReadyForTour();
        }

        // Remove initializing state
        this.tourInitializing = false;
        document.body.classList.remove('tour-initializing');
      }, 100);
    });
  }

  ngAfterViewInit(): void {
    // console.log('ICP Optimization view initialized');

    // Check if the ICP form element exists and log its details
    const formElement = document.querySelector('#icp-optimization-form');
    // console.log('Form element found?', !!formElement);

    /*
    if (formElement) {
      console.log('Form element details:', {
        id: formElement.id,
        classList: Array.from(formElement.classList),
        width: (formElement as HTMLElement).clientWidth,
        height: (formElement as HTMLElement).clientHeight,
        visible: !!(formElement as HTMLElement).offsetParent
      });
    }
    */

    // Always ensure form is ready for tour
    this.ensureFormReadyForTour();
  }

  /**
   * Fast tour state check at the very beginning
   */
  private fastCheckTourState(): void {
    // Direct localStorage check - faster than store subscription
    const tourInProgress = localStorage.getItem('salesommo_tour_in_progress') === 'true';
    const icpTourCompleted = localStorage.getItem('salesommo_tour_completed_icp') === 'true';

    if (tourInProgress && !icpTourCompleted) {
      // Tour is in progress and not completed - prepare immediately
      console.log('Tour in progress detected, preparing immediately');

      // Set active state directly
      this.isTourActive = true;

      // Add body class immediately
      document.body.classList.add('tour-active');

      // No need to wait for anything - prepare form right away
      requestAnimationFrame(() => {
        const formElement = document.querySelector('#icp-optimization-form') as HTMLElement;
        if (formElement) {
          // Apply all styles at once
          Object.assign(formElement.style, {
            visibility: 'visible',
            opacity: '1',
            overflow: 'auto',
            maxHeight: '80vh',
            zIndex: '10000',
            position: 'relative'
          });

          // Force element to be visible
          formElement.classList.remove('invisible', 'opacity-0');
          formElement.classList.add('overflow-y-auto', 'max-h-[80vh]', 'thin-scrollbar');
        }
      });
    }
  }

  /**
   * Prepare the form for scrolling during tour - improved for performance
   */
  private prepareFormForScrolling(): void {
    // Make this happen immediately
    requestAnimationFrame(() => {
      const formElement = document.querySelector('#icp-optimization-form') as HTMLElement;
      if (formElement) {
        // Store original styles
        this.originalOverflowStyle = formElement.style.overflow;
        this.originalMaxHeight = formElement.style.maxHeight;

        // Apply styles in a single operation for better performance
        Object.assign(formElement.style, {
          overflow: 'auto',
          maxHeight: '80vh'
        });

        // Add classes directly
        formElement.classList.add('overflow-y-auto', 'max-h-[80vh]', 'thin-scrollbar');
      }
    });
  }

  /**
   * Ensure the form is ready for tour - called when tour becomes active
   */
  private ensureFormReadyForTour(): void {
    // If tour is active, form must be visible regardless of data state
    if (this.isTourActive) {
      // Force form to be visible regardless of data loading
      const formElement = document.querySelector('#icp-optimization-form') as HTMLElement;
      if (formElement) {
        formElement.classList.remove('invisible');
        formElement.style.visibility = 'visible';
        formElement.style.opacity = '1';

        // Add tour-active class to body if not already
        if (!document.body.classList.contains('tour-active')) {
          document.body.classList.add('tour-active');
        }
      }
    }
  }

  private initializeForm(): void {
    this.form = this.formBuilder.group({
      // Step 1 fields
      name: ["", [Validators.required, Validators.maxLength(255)]],
      description: ["", [Validators.maxLength(MAX_ICP_TEXT_LENGTH)]],
      linkedin_url: ["", [
        Validators.required,
        Validators.pattern(/^(https?:\/\/)?(www\.)?linkedin\.com\/.*$/),
        Validators.maxLength(255)
      ]],
      what_solutions_do_i_sell: ["", [Validators.required, Validators.maxLength(MAX_ICP_LONG_TEXT_LENGTH)]],
      how_i_help_my_customers: ["", [Validators.maxLength(255)]],

      // Step 2 fields
      customer_company_size_ids: [[], [Validators.required]],
      company_revenue_ids: [[], [Validators.required]],
      customer_industry_ids: [[], [Validators.required]],
      challenges_solved: ["", [Validators.required, Validators.maxLength(MAX_ICP_LONG_TEXT_LENGTH)]],
      target_country_ids: [[]],
      main_buyer_department: ["", [Validators.required, Validators.maxLength(MAX_ICP_TARGETING_LENGTH)]],
      main_buyer_job_title: ["", [Validators.required, Validators.maxLength(MAX_ICP_TARGETING_LENGTH)]],
      country_exclusion_ids: [[]],
    });
  }

  private setupErrorMessages(): void {
    this.errorMessages = {
      // Step 1 messages
      name: [
        {type: "required", message: "Name is required"},
        {type: "maxlength", message: "Name cannot exceed 255 characters"}
      ],
      description: [{type: "maxlength", message: "Description cannot exceed 512 characters"}],
      linkedin_url: [
        {type: "required", message: "LinkedIn URL is required"},
        {type: "maxlength", message: "LinkedIn URL cannot exceed 255 characters"},
        {type: "pattern", message: "Please enter a valid LinkedIn URL"}
      ],
      what_solutions_do_i_sell: [{type: "required", message: "What solutions do I sell is required"}, {type: "maxlength", message: "What solutions do I sell cannot exceed 1024 characters"}],
      how_i_help_my_customers: [{type: "maxlength", message: "Help customers cannot exceed 255 characters"}],

      // Step 2 messages
      customer_company_size_ids: [{type: "required", message: "Company size is required"}],
      company_revenue_ids: [{type: "required", message: "Company revenue is required"}],
      customer_industry_ids: [{type: "required", message: "Industry is required"}],
      challenges_solved: [{type: "required", message: "Challenges solved is required"}, {type: "maxlength", message: "Challenges solved cannot exceed 1024 characters"}],
      target_country_ids: [{type: "maxlength", message: "Target countries cannot exceed 255 characters"}],
      main_buyer_department: [{type: "required", message: "Target Departments is required"}, {type: "maxlength", message: "Target Departments cannot exceed 512 characters"}],
      main_buyer_job_title: [{type: "required", message: "Target Job Titles is required"}, {type: "maxlength", message: "Target Job Titles cannot exceed 512 characters"}],
      country_exclusion_ids: [{type: "maxlength", message: "Country exclusion cannot exceed 255 characters"}],
    };
  }

  // Set up form listeners for reactive validation
  private setupFormListeners(): void {
    // Get all the step 1 form controls
    const step1Controls = [
      'name',
      'linkedin_url',
      'what_solutions_do_i_sell'
    ];

    // Add value change subscription for each control
    step1Controls.forEach(controlName => {
      const control = this.form.get(controlName);
      if (control) {
        this.subscriptions.add(
          control.valueChanges.subscribe(() => {
            // Clear validation cache to force re-validation
            this.resetValidationCache();

            // If we're updating the LinkedIn URL and it's valid, save it
            if (controlName === 'linkedin_url' && control.valid) {
              this.checkAndSaveLinkedInUrl();
            }
          })
        );
      }
    });

    // Also listen for status changes (valid/invalid)
    this.subscriptions.add(
      this.form.statusChanges.subscribe(() => {
        this.resetValidationCache();
      })
    );
  }

  ngOnDestroy() {
    // Cleanup subscriptions
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.unsubscribe();

    // Additional cleanup for tour
    if (document.body.classList.contains('tour-active')) {
      document.body.classList.remove('tour-active');
    }

    if (document.body.classList.contains('tour-initializing')) {
      document.body.classList.remove('tour-initializing');
    }

    // Clean up Tailwind classes too
    const formElement = document.querySelector('#icp-optimization-form') as HTMLElement;
    if (formElement) {
      formElement.classList.remove('overflow-y-auto');
      formElement.classList.remove('max-h-[80vh]');
      formElement.classList.remove('thin-scrollbar');
      formElement.style.overflow = this.originalOverflowStyle || '';
      formElement.style.maxHeight = this.originalMaxHeight || '';
    }
  }

  // Method to check and save LinkedIn URL if needed
  private checkAndSaveLinkedInUrl(): void {
    // Only proceed if user doesn't have a LinkedIn URL and the field has a valid value
    if (!this.user?.linkedin_url &&
      this.form.get('linkedin_url')?.enabled &&
      this.form.get('linkedin_url')?.valid) {

      const linkedInValue = this.form.get('linkedin_url')?.value;
      if (linkedInValue) {
        // If we're about to proceed to the next step with a valid LinkedIn URL,
        // update the user object to include it
        this.user = { ...this.user, linkedin_url: linkedInValue };
      }
    }
  }

  // Handle selection changes for multiselect components
  handleCustomerIndustrySelectionChange(selectedCustomerIndustries: any[]): void {
    const selectedCustomerIndustryIds = selectedCustomerIndustries.map(industry => industry.id);
    this.form.patchValue({ customer_industry_ids: selectedCustomerIndustryIds });
    this.resetValidationCache();
  }

  handleCustomerCompanySizeSelectionChange(selectedCustomerCompanySizes: any[]): void {
    const selectedCustomerCompanySizeIds = selectedCustomerCompanySizes.map(size => size.id);
    this.form.patchValue({ customer_company_size_ids: selectedCustomerCompanySizeIds });
    this.resetValidationCache();
  }

  handleCompanyRevenueSelectionChange(selectedCompanyRevenues: any[]): void {
    const selectedCompanyRevenueIds = selectedCompanyRevenues.map(revenue => revenue.id);
    this.form.patchValue({ company_revenue_ids: selectedCompanyRevenueIds });
    this.resetValidationCache();
  }

  handleTargetCountrySelectionChange(selectedTargetCountries: any[]): void {
    const selectedTargetCountryIds = selectedTargetCountries.map(country => country.id);
    this.form.patchValue({ target_country_ids: selectedTargetCountryIds });
    this.resetValidationCache();
  }

  handleCountryExclusionListSelectionChange(selectedCountryExclusionList: any[]): void {
    const selectedCountryExclusionIds = selectedCountryExclusionList.map(country => country.id);
    this.form.patchValue({ country_exclusion_ids: selectedCountryExclusionIds });
    this.resetValidationCache();
  }

  toggleJobTitle(title: string): void {
    if (this.selectedJobTitles.has(title)) {
      this.selectedJobTitles.delete(title);
    } else {
      this.selectedJobTitles.add(title);
    }
    this.syncJobTitlesToForm();
  }

  isJobTitleSelected(title: string): boolean {
    return this.selectedJobTitles.has(title);
  }

  removeJobTitle(title: string): void {
    this.selectedJobTitles.delete(title);
    this.syncJobTitlesToForm();
  }

  toggleDepartment(department: string): void {
    if (this.selectedDepartments.has(department)) {
      this.selectedDepartments.delete(department);
    } else {
      this.selectedDepartments.add(department);
    }
    this.syncDepartmentsToForm();
  }

  isDepartmentSelected(department: string): boolean {
    return this.selectedDepartments.has(department);
  }

  removeDepartment(department: string): void {
    this.selectedDepartments.delete(department);
    this.syncDepartmentsToForm();
  }

  // ── Custom "Other" entries (free text, 255 max each) ──
  readonly otherOptionId = OTHER_OPTION_ID;
  readonly maxCustomTargetLength = MAX_CUSTOM_TARGET_LENGTH;
  customJobTitleInput = '';
  customDepartmentInput = '';

  get isOtherJobTitleSelected(): boolean {
    return this.selectedJobTitles.has(OTHER_OPTION_ID);
  }

  get isOtherDepartmentSelected(): boolean {
    return this.selectedDepartments.has(OTHER_OPTION_ID);
  }

  addCustomJobTitle(): void {
    const value = sanitizeCustomTarget(this.customJobTitleInput);
    this.customJobTitleInput = '';

    if (!value || this.hasSelection(this.selectedJobTitles, value)) {
      return;
    }

    this.selectedJobTitles.add(value);
    this.syncJobTitlesToForm();
  }

  addCustomDepartment(): void {
    const value = sanitizeCustomTarget(this.customDepartmentInput);
    this.customDepartmentInput = '';

    if (!value || this.hasSelection(this.selectedDepartments, value)) {
      return;
    }

    this.selectedDepartments.add(value);
    this.syncDepartmentsToForm();
  }

  private hasSelection(selected: Set<string>, value: string): boolean {
    const lower = value.toLowerCase();

    return Array.from(selected).some(entry => entry.toLowerCase() === lower);
  }

  private syncJobTitlesToForm(): void {
    this.form.patchValue({ main_buyer_job_title: selectionsToCsv(this.selectedJobTitles) });
    this.resetValidationCache();
  }

  private syncDepartmentsToForm(): void {
    this.form.patchValue({ main_buyer_department: selectionsToCsv(this.selectedDepartments) });
    this.resetValidationCache();
  }

  private hydrateTargetingChips(jobTitles?: string | null, departments?: string | null): void {
    this.selectedJobTitles = parseCsvSelections(jobTitles, this.targetJobTitleOptions);
    this.selectedDepartments = parseCsvSelections(departments, this.targetDepartmentOptions);

    if (customSelections(this.selectedJobTitles, this.targetJobTitleOptions).length) {
      this.selectedJobTitles.add(OTHER_OPTION_ID);
    }
    if (customSelections(this.selectedDepartments, this.targetDepartmentOptions).length) {
      this.selectedDepartments.add(OTHER_OPTION_ID);
    }
    this.form.patchValue({
      main_buyer_job_title: selectionsToCsv(this.selectedJobTitles),
      main_buyer_department: selectionsToCsv(this.selectedDepartments),
    }, { emitEvent: false });
    this.resetValidationCache();
  }

  // Reset validation cache when form changes
  private resetValidationCache(): void {
    this.validationCache = {};
  }

  // Navigation and validation methods
  goToNextStep(): void {
    // Check if LinkedIn URL needs to be saved before proceeding
    this.checkAndSaveLinkedInUrl();

    // Now proceed with normal validation
    if (this.isStepValid(1)) {
      this.changeStep(2);

      // Scroll to top after changing step
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });

      // Also scroll the form container to top if it has its own scroll
      const formElement = document.getElementById('icp-optimization-form');
      if (formElement) {
        formElement.scrollTop = 0;
      }
    } else {
      // Reports the specific offending field rather than a generic "fill in all required fields".
      this.handleInvalidForm();
    }
  }

  changeStep(nextStep: number): void {
    if (nextStep > this.step) {
      // Moving forward - validate current step
      if (this.isStepValid(this.step)) {
        this.step = nextStep;
        // Reset validation cache when changing steps
        this.resetValidationCache();
      } else {
        // Reports the specific offending field rather than a generic "fill in all required fields".
        this.handleInvalidForm();
      }
    } else {
      // Moving backward - no validation needed
      this.step = nextStep;
      // Reset validation cache when changing steps
      this.resetValidationCache();
    }
  }

  // Optimized step validation with caching
  isStepValid(step: number): boolean {
    // Return false if data isn't loaded yet
    if (!this.dataLoaded) {
      return false;
    }

    // Check cache first
    const cacheKey = `step_${step}`;
    if (this.validationCache[cacheKey] !== undefined) {
      return this.validationCache[cacheKey];
    }

    // Calculate validation result based on step
    let isValid = false;

    if (step === 1) {
      // Handle LinkedIn URL validation specially
      const linkedinIsValid = this.isLinkedInUrlValid();

      isValid = this.form.get('name')?.valid &&
        this.form.get('description')?.valid &&
        linkedinIsValid &&
        this.form.get('what_solutions_do_i_sell')?.valid;
    } else if (step === 2) {
      isValid = this.form.get('customer_company_size_ids')?.valid &&
        this.form.get('company_revenue_ids')?.valid &&
        this.form.get('customer_industry_ids')?.valid &&
        this.form.get('challenges_solved')?.valid &&
        this.form.get('main_buyer_department')?.valid &&
        this.form.get('main_buyer_job_title')?.valid;
    }

    // Store result in cache
    this.validationCache[cacheKey] = isValid;
    return isValid;
  }

  // Special method to handle LinkedIn URL validation logic
  private isLinkedInUrlValid(): boolean {
    // If user already has a valid LinkedIn URL in their profile
    if (this.user?.linkedin_url) {
      const linkedinRegex = /^(https?:\/\/)?(www\.)?linkedin\.com\/.*$/;
      if (linkedinRegex.test(this.user.linkedin_url)) {
        return true;
      }
    }

    // If the LinkedIn form control is disabled, it means it was validated and accepted
    if (this.form.get('linkedin_url')?.disabled) {
      return true;
    }

    // Otherwise, check if the input field has a valid value
    return !!this.form.get('linkedin_url')?.valid;
  }

  // Helper method to show validation error
  private showValidationError(): void {
    this.popupService.notify(
      'Please fill in all required fields before proceeding.',
      'Validation Error',
      'error'
    );
  }

  // Keeps an invalid payload on the client: shows the error on the offending field instead of letting the API reject it.
  private handleInvalidForm(): void {
    // isStepValid() also reports false while the reference lists are still loading, when no control is
    // actually invalid yet. Say so instead of blaming whichever field the scan happens to land on.
    if (!this.dataLoaded) {
      this.popupService.notify(
        'Please wait for the form to finish loading, then try again.',
        'Still loading',
        'info'
      );
      return;
    }

    this.form.markAllAsTouched();
    this.resetValidationCache();

    const invalidField = this.getFirstInvalidField();

    if (!invalidField) {
      this.showValidationError();
      return;
    }

    const targetStep = this.getStepForField(invalidField);

    // Only ever move back. This runs when navigation or submit was blocked, so advancing would carry the user
    // past the step that just failed. Set the step directly rather than going through changeStep: a forward
    // move there re-validates and would emit its own competing error notification.
    if (targetStep !== null && targetStep < this.step) {
      this.step = targetStep;
      this.resetValidationCache();
    }

    this.popupService.notify(
      this.getErrorMessageForField(invalidField),
      'Validation Error',
      'error'
    );

    // Deferred so the field exists in the DOM after a step switch.
    setTimeout(() => this.scrollToField(invalidField), 100);
  }

  // Scans the two steps in visual order first, then every remaining control. Fields that are not rendered can still
  // hold server data that fails validation, and leaving them out would block saving with no indication of the cause.
  private getFirstInvalidField(): string | null {
    const ordered = [...this.stepOneFields, ...this.stepTwoFields];
    const remaining = Object.keys(this.form.controls).filter(field => !ordered.includes(field));

    return [...ordered, ...remaining]
      .find(field => {
        const control = this.form.get(field);

        return !!control && control.enabled && control.invalid;
      }) ?? null;
  }

  private getStepForField(field: string): number | null {
    if (this.stepOneFields.includes(field)) {
      return 1;
    }

    return this.stepTwoFields.includes(field) ? 2 : null;
  }

  private getErrorMessageForField(field: string): string {
    const control = this.form.get(field);
    const messages: { type: string, message: string }[] = this.errorMessages?.[field] ?? [];
    const match = messages.find(entry => control?.hasError(entry.type));

    if (match) {
      return match.message;
    }

    // Names the control so an unmapped field is still actionable instead of an opaque "something is wrong".
    return `${field.replace(/_/g, ' ')} is invalid. Please correct it before saving.`;
  }

  private scrollToField(field: string): void {
    const element = document.getElementById(field);

    if (!element) {
      return;
    }

    element.scrollIntoView({behavior: 'smooth', block: 'center'});
    element.focus({preventScroll: true});
  }

  // Update form with profile data
  updateForm(profile: any): void {
    if (profile) {
      // Prepare form data
      const formData = {
        name: profile.name || '',
        description: profile.description || '',
        linkedin_url: this.user.linkedin_url,
        what_solutions_do_i_sell: profile.what_solutions_do_i_sell,
        how_i_help_my_customers: profile.how_i_help_my_customers || '-',

        customer_company_size_ids: profile.customer_company_size_ids,
        company_revenue_ids: profile.company_revenue_ids,
        customer_industry_ids: profile.customer_industry_ids,
        challenges_solved: profile.challenges_solved,
        target_country_ids: profile.target_country_ids,

        main_buyer_department: profile.main_buyer_department,
        main_buyer_job_title: profile.main_buyer_job_title,
        country_exclusion_ids: profile.country_exclusion_ids,
      };

      // Update form values
      this.form.patchValue(formData);
      this.hydrateTargetingChips(formData.main_buyer_job_title, formData.main_buyer_department);

      // Update multiselect components if data exists
      if (profile.customer_industries) {
        this.handleCustomerIndustrySelectionChange(profile.customer_industries);
      }
      if (profile.customer_company_sizes) {
        this.handleCustomerCompanySizeSelectionChange(profile.customer_company_sizes);
      }
      if (profile.company_revenues) {
        this.handleCompanyRevenueSelectionChange(profile.company_revenues);
      }
      if (profile.target_countries) {
        this.handleTargetCountrySelectionChange(profile.target_countries);
      }
      if (profile.country_exclusions) {
        this.handleCountryExclusionListSelectionChange(profile.country_exclusions);
      }

      // Reset validation cache after form update and trigger validation check
      this.resetValidationCache();

      // Apply form changes after a short delay to allow Angular to process the updates
      setTimeout(() => {
        this.form.updateValueAndValidity();
      }, 0);
    }
  }

  private prefillFromDefaultIcp(defaultIcp: any): void {
    if (!defaultIcp) return;

    // Only prefill kept "Who I am" fields — skip name, description, linkedin_url.
    this.form.patchValue({
      what_solutions_do_i_sell: defaultIcp.what_solutions_do_i_sell ?? '',
      how_i_help_my_customers: defaultIcp.how_i_help_my_customers ?? '',
    });

    this.resetValidationCache();
    setTimeout(() => this.form.updateValueAndValidity(), 0);
  }

  // Submit form data
  submitForm(): void {
    // Check and save LinkedIn URL if needed
    this.checkAndSaveLinkedInUrl();

    // Validate current step
    if (!this.isStepValid(this.step)) {
      this.handleInvalidForm();
      return;
    }

    // Validate entire form
    if (this.form.valid) {
      // Additional validation for required fields
      const requiredFields = ['what_solutions_do_i_sell', 'challenges_solved', 'main_buyer_job_title', 'main_buyer_department'];
      const missingFields = requiredFields.filter(field => !this.form.value[field] || this.form.value[field].trim() === '');

      if (missingFields.length > 0) {
        this.popupService.notify(
          `Please fill in all required fields: ${missingFields.join(', ')}`,
          'Validation Error',
          'error'
        );
        return;
      }
      // Name is required and capped at 255 by the form, so the validated value is used as-is.
      const icpName = this.form.value.name;
      // Only the generated fallback needs clamping — it combines two fields and can outgrow the limit on its own,
      // whereas a user-entered description is already bounded by the validator.
      const icpDescription = String(this.form.value.description ?? '').trim()
        || `Targeting ${this.form.value.main_buyer_job_title || 'buyers'} — ${this.form.value.challenges_solved}`.slice(0, MAX_ICP_TEXT_LENGTH);

      // Prepare ICP data from form values - using standard field names as expected by ICP creation API
      const icpData = {
        name: icpName,
        description: icpDescription,
        is_default: !this.hasExistingIcp, // Set to true only for first ICP, false for subsequent ones
        what_solutions_do_i_sell: this.form.value.what_solutions_do_i_sell || '-',
        how_i_help_my_customers: this.form.value.how_i_help_my_customers || '-',
        target_company_size_ids: this.form.value.customer_company_size_ids || [],
        target_company_revenue_ids: this.form.value.company_revenue_ids || [],
        user_company_revenue_ids: [1], // Additional field required by API - N/A (ID: 1)
        target_industry_ids: this.form.value.customer_industry_ids || [],
        challenges_solved: this.form.value.challenges_solved || '-',
        target_country_ids: this.form.value.target_country_ids || [],
        main_buyer_department: this.form.value.main_buyer_department,
        main_buyer_job_title: this.form.value.main_buyer_job_title,
        country_exclusion_ids: this.form.value.country_exclusion_ids || [],
      };

      // Dispatch action to create ICP
      this.store.dispatch(IcpActions.createIcp({ icp: icpData }));

      // Also submit the user profile data (commented on: 04.11.2025 because the ICP functionality has changed)
      // const profileData = {
      //   user_id: Number(this.user.id),
      //   linkedin_url: this.user.linkedin_url || this.form.value.linkedin_url,
      //   ...icpData
      // };
      // this.store.dispatch(submitUserProfile({ profileData }));

      // Handle ICP creation success
      this.subscriptions.add(
        this.store.select(IcpSelectors.selectCreateIcpSuccess)
          .pipe(filter(success => success === true), take(1))
          .subscribe(() => {
            // Update user with new LinkedIn URL if provided
            const updatedUser = {
              ...this.user,
              linkedin_url: this.user.linkedin_url || this.form.value.linkedin_url,
              has_completed_setup: 1 // Mark setup as completed
            };
            this.store.dispatch(updateUser({ user: updatedUser }));

            // Check if tour is active and complete it
            if (this.tourService.isActiveTour()) {
              console.log('Completing tour after successful form submission');
              localStorage.setItem('appTourCompleted', 'true');
              localStorage.setItem('salesommo_tour_completed_dashboard', 'true');
              localStorage.setItem('salesommo_tour_completed_icp', 'true');
              this.appTourService.completeTour();
              console.log('All tours marked as completed');
            }

            // Navigation is handled by the effect
          })
      );

      // Handle ICP creation error
      this.subscriptions.add(
        this.store.select(IcpSelectors.selectCreateIcpError)
          .pipe(filter(error => !!error))
          .subscribe(error => {
            // Error notification is handled by the effect
          })
      );
    } else {
      this.handleInvalidForm();
    }
  }

  getStepStatusMessage(step: number): string {
    return this.isStepValid(step) ? 'Inputs are populated' : 'Inputs are not populated';
  }

  // Data loading methods
  private getProfileLists(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.store.dispatch(loadProfileLists());

      // Success handler
      this.subscriptions.add(
        this.store.select(selectProfileListsSuccess)
          .pipe(filter(response => response !== null))
          .subscribe(response => {
            this.profileLists = response.profileLists;
            this.actualDuties = response.profileLists.actual_duties;
            this.companySizes = response.profileLists.company_sizes;
            // ICP target relationships (targetCompanySizes/targetIndustries) reference the
            // CompanySize/Industry models, so the customer side must use the same lists.
            this.customerCompanySizes = response.profileLists.company_sizes;
            this.companyRevenues = response.profileLists.company_revenues;
            this.industries = response.profileLists.industry;
            this.customerIndustries = response.profileLists.industry;
            this.buyingCycles = response.profileLists.buying_cycle;
            this.trendEmployees = response.profileLists.trend_employee;
            this.trendRevenues = response.profileLists.trend_revenue;
            this.closedDeals = response.profileLists.close_deal;
            resolve();
          })
      );

      // Error handler
      this.subscriptions.add(
        this.store.select(selectProfileListsFailure)
          .pipe(filter(error => error !== null))
          .subscribe(() => {
            // Resolve even on error to prevent hanging
            resolve();
          })
      );
    });
  }

  private getCountries(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.store.dispatch(loadCountries());

      // Success handler
      this.subscriptions.add(
        this.store.select(selectCountriesSuccess)
          .pipe(filter(response => response !== null))
          .subscribe(response => {
            this.countries = response.countries;
            resolve();
          })
      );

      // Error handler
      this.subscriptions.add(
        this.store.select(selectCountriesFailure)
          .pipe(filter(error => error !== null))
          .subscribe(() => {
            // Resolve even on error to prevent hanging
            resolve();
          })
      );
    });
  }

  // Sidebar toggler
  toggleMobileSidebar(): void {
    this.sidebarService.toggleMobileSidebar();
  }

  /**
   * Get button classes based on step state
   */
  getStepButtonClass(stepNumber: number): string {
    const isActive = this.step === stepNumber;
    const isValid = this.isStepValid(stepNumber);

    const baseClasses = 'flex items-center justify-start space-x-3 rounded-lg px-6 py-3 text-sm font-medium tracking-wide w-full sm:w-auto';

    if (isActive) {
      return `${baseClasses} ${isValid ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`;
    } else {
      return `${baseClasses} ${isValid ? 'text-green-600' : 'text-gray-300'}`;
    }
  }

  /**
   * Get icon color class based on validation state
   */
  getIconColorClass(stepNumber: number): string {
    return this.isStepValid(stepNumber) ? 'text-green-600' : 'text-amber-500';
  }

}
